import Header from "@/components/Header";
import Footer from "@/components/Footer";

/**
 * Three static policy pages — About, Refund, Privacy.
 *
 * Copy is a JDPA 2020-aligned DRAFT.  Replace any of the paragraphs below
 * verbatim once Legal/Marketing finalises the wording — content lives at the
 * top of each component, no logic mixed in.
 */

function PolicyShell({ title, kicker, children, testId }) {
    return (
        <div className="min-h-screen bg-background" data-testid={testId}>
            <Header />
            <article className="max-w-3xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
                <div className="overline mb-4">{kicker}</div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-12">
                    {title}
                </h1>
                <div className="prose prose-invert max-w-none policy-body">
                    {children}
                </div>
                <div className="mt-16 pt-8 border-t border-white/5 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                    Last updated · February 2026
                </div>
            </article>
            <Footer />
        </div>
    );
}

export function AboutPage() {
    return (
        <PolicyShell title="About SMART" kicker="Our story" testId="about-page">
            <p>
                SMART (operated by <strong>SMART DEMANDS 24/7 LIMITED</strong>, a
                company duly incorporated under the Laws of Jamaica) is the
                island's premium on-demand concierge — a single app that
                connects households, hotels and businesses with verified
                professionals across cleaning, glam, childcare, private chefs,
                grocery, handymen, transport and beyond.
            </p>
            <p>
                We built SMART because Jamaicans deserve world-class service that
                respects local realities: distance pricing that's fair, providers
                that are vetted (TRN, ID, police record, address), and a 24/7
                response promise. Every booking funds a living wage for the pro
                doing the work — we keep our take simple at 15% so providers earn
                more, faster.
            </p>
            <p>
                The team is small, Jamaican, and obsessed with the details that
                make a service feel premium: the orange-uniformed cleaner who
                arrives with a smile, the chef who leaves the kitchen cleaner than
                they found it, the driver who knows every shortcut from Ochi to
                Negril. That's the SMART standard.
            </p>
            <p>
                Questions, suggestions or partnership ideas?
                Email <a href="mailto:customercare@smartdemands247.com" className="gold-text">customercare@smartdemands247.com</a>.
            </p>
        </PolicyShell>
    );
}

export function RefundPolicyPage() {
    return (
        <PolicyShell title="Refund Policy" kicker="All Sales Are Final" testId="refund-page">
            <div className="p-5 mb-8 bg-primary/10 border border-primary/40 rounded-sm">
                <div className="overline gold-text mb-2">Absolute Policy</div>
                <p className="text-base text-foreground font-semibold leading-relaxed m-0">
                    ALL SALES ARE FINAL. Once a booking is confirmed and payment
                    is processed, no refunds, credits, exchanges, or chargebacks
                    will be issued — for any reason, at any time.
                </p>
            </div>

            <p>
                This Refund Policy ("Policy") forms part of the Terms of
                Service governing your use of the SMART platform, owned and
                operated by <strong>SMART DEMANDS 24/7 LIMITED</strong> (the
                "Company", "we", "us", or "our"), a company duly incorporated
                under the Laws of Jamaica. By creating a SMART account, placing
                a booking, paying for a service, or otherwise using the
                platform, you irrevocably accept this Policy in full.
            </p>

            <h2>1. No Refunds — Absolute Rule</h2>
            <p>
                All payments — including but not limited to deposits, full
                service payments, transport fees, Royal Protection &amp;
                Insurance fees, platform commissions, add-on charges,
                subscription fees, VIP membership fees, gratuities, and any
                ancillary charge — are <strong>final, binding, and
                non-refundable</strong> from the moment payment is authorised,
                regardless of:
            </p>
            <ul>
                <li>Whether the customer cancels the booking;</li>
                <li>Whether the customer fails to be present at the service location;</li>
                <li>Whether the customer changes their mind, schedule, or address;</li>
                <li>Subjective dissatisfaction with the service rendered;</li>
                <li>Weather conditions, traffic, or other external factors;</li>
                <li>Customer-side technical issues (app, payment method, connectivity).</li>
            </ul>

            <h2>2. No Cancellations With Refund</h2>
            <p>
                Bookings cannot be cancelled for a refund. Customers may
                <em> reschedule </em> a booking once at no additional charge
                provided the request is made at least 24 hours before the
                scheduled service start time, and subject to provider
                availability. Reschedule credits are non-transferable and
                non-refundable.
            </p>

            <h2>3. Service-Level Issues</h2>
            <p>
                While we do not issue refunds, SMART stands behind the quality
                of our verified professionals. If a service is alleged to be
                materially incomplete or undelivered, the customer must open a
                support ticket from the booking page <strong>within 24 hours</strong>
                of the scheduled service completion. SMART will, at its sole
                and absolute discretion, investigate and may — but is not
                obligated to — provide one of the following remedies:
            </p>
            <ul>
                <li>Dispatch a different provider to complete the outstanding work, or</li>
                <li>Issue non-cash, non-transferable SMART platform credit toward a future booking, expiring 30 days from issue.</li>
            </ul>
            <p>
                These remedies are gestures of goodwill, granted entirely at
                the Company's discretion, and do not constitute, imply, or
                create any right to a monetary refund.
            </p>

            <h2>4. No Chargebacks</h2>
            <p>
                The customer agrees not to initiate, threaten, or pursue any
                chargeback, payment dispute, or reversal with their bank, card
                issuer, or payment processor in respect of any transaction
                with the Company. Any chargeback initiated in breach of this
                Policy will be treated as a material breach of the Terms of
                Service and may result in: (a) immediate, permanent account
                termination; (b) loss of all SMART platform credits, loyalty
                points, and SMART Coins; (c) recovery of the disputed sum plus
                administrative costs and legal fees; and (d) referral to a
                debt-collection agency or the courts of Jamaica.
            </p>

            <h2>5. Subscriptions, VIP &amp; Memberships</h2>
            <p>
                Recurring subscriptions and VIP membership fees are billed in
                advance and are <strong>fully non-refundable</strong>, including
                for any unused portion of the cycle. The customer may cancel
                future renewals at any time from their account settings;
                cancellation will take effect at the end of the then-current
                billing cycle.
            </p>

            <h2>6. SMART Coins, Promo Codes &amp; Credits</h2>
            <p>
                SMART Coins, referral credits, promotional codes (including
                <code> ROYAL10</code>, <code>ROYAL15</code>) and platform credits
                hold no cash value, are non-refundable, non-transferable,
                non-withdrawable, and may be revoked at any time at the
                Company's sole discretion.
            </p>

            <h2>7. Bookings by Venue Partners &amp; Third Parties</h2>
            <p>
                Where a booking is created on a customer's behalf by a Venue
                Partner, the venue is the contracting party and bears full
                responsibility for the booking. The end-guest has no direct
                refund claim against the Company.
            </p>

            <h2>8. Statutory Rights</h2>
            <p>
                Nothing in this Policy is intended to exclude, restrict, or
                modify any consumer right that, under the Consumer Protection
                Act, 2005 of Jamaica, cannot lawfully be excluded. Where any
                provision of this Policy is found to be inconsistent with such
                a right, the provision shall be deemed modified only to the
                minimum extent necessary to comply with that right, with all
                remaining provisions remaining in full force and effect.
            </p>

            <h2>9. Acceptance</h2>
            <p>
                By tapping <em>Pay</em>, <em>Confirm Booking</em>,
                <em> Subscribe</em>, or otherwise completing any payment on the
                SMART platform, the customer confirms that they have read,
                understood, and irrevocably accept this Refund Policy. If you
                do not accept this Policy, do not proceed with payment.
            </p>

            <h2>10. Contact</h2>
            <p>
                Questions about this Policy should be directed to
                <a href="mailto:customercare@smartdemands247.com" className="gold-text"> customercare@smartdemands247.com</a>.
                Operational service tickets must be raised from the booking
                page within the disclosed 24-hour window. Submission of a
                support enquiry does not entitle the customer to a refund.
            </p>

            <div className="mt-10 p-4 bg-card border border-white/10 rounded-sm text-[11px] text-muted-foreground leading-relaxed">
                Operator: <strong>SMART DEMANDS 24/7 LIMITED</strong> · A company duly incorporated under the Laws of Jamaica · Customer Care: <a href="mailto:customercare@smartdemands247.com" className="gold-text">customercare@smartdemands247.com</a> · Support line: <a href="https://wa.me/18765028155" className="gold-text">+1 876-502-8155 (WhatsApp)</a>.
            </div>
        </PolicyShell>
    );
}

export function PrivacyPolicyPage() {
    return (
        <PolicyShell title="Privacy Policy" kicker="JDPA 2020 compliant" testId="privacy-page">
            <p>
                SMART (operated by <strong>SMART DEMANDS 24/7 LIMITED</strong>,
                the "Company") is a registered data controller under Jamaica's
                <em> Data Protection Act, 2020 (JDPA)</em>. This policy explains
                what personal data we collect, why, how we protect it, and the
                rights you have over it.
            </p>

            <h2>1. What we collect</h2>
            <ul>
                <li><strong>Account data</strong> — name, email, phone, role.</li>
                <li><strong>Booking data</strong> — service address, scheduled date/time, instructions you write to your provider.</li>
                <li><strong>Payment data</strong> — handled by our PCI-DSS-compliant processor (Stripe). SMART never stores your full card number.</li>
                <li><strong>Verification data</strong> (providers only) — TRN, government ID, police record, proof of address. Stored encrypted; only viewable by SMART admins for vetting.</li>
                <li><strong>Location data</strong> (optional) — your live coordinates only when you explicitly tap "use my location" for distance pricing or safety pings.</li>
                <li><strong>Device & usage</strong> — IP, device type, browser, pages viewed (to keep the service running and prevent fraud).</li>
            </ul>

            <h2>2. Why we use it</h2>
            <ul>
                <li>To match you with the right provider and bill the correct distance fee.</li>
                <li>To verify identity (KYC) for provider onboarding and vehicle owner listings.</li>
                <li>To send transactional notifications (booking confirmations, safety pings, password resets).</li>
                <li>To investigate complaints, fraud, or safety incidents.</li>
                <li>To improve the platform — anonymised analytics only.</li>
            </ul>

            <h2>3. Who we share it with</h2>
            <p>
                Only with providers you book (limited to first name, masked
                phone, address you booked at) and our processors (Stripe for
                payments, Resend for email, Twilio for SMS). We do not sell
                your data to third parties for marketing.
            </p>

            <h2>4. How long we keep it</h2>
            <p>
                Account data: as long as your account is active. Booking and
                financial records: 7 years (Jamaican tax retention requirement).
                Provider verification documents: deleted within 90 days of
                provider account closure.
            </p>

            <h2>5. Your rights under JDPA 2020</h2>
            <p>You have the right to:</p>
            <ul>
                <li><strong>Access</strong> the personal data we hold about you.</li>
                <li><strong>Correct</strong> any inaccurate data.</li>
                <li><strong>Erase</strong> your data ("right to be forgotten") — except where we must retain it by law (e.g. tax records).</li>
                <li><strong>Object</strong> to specific processing (e.g. marketing emails).</li>
                <li><strong>Port</strong> your data to another service provider.</li>
                <li><strong>Withdraw consent</strong> at any time; if you do we'll close your account on request.</li>
            </ul>
            <p>
                Send any request to <a href="mailto:customercare@smartdemands247.com" className="gold-text">customercare@smartdemands247.com</a>.
                We respond within 30 days as required by JDPA s.6.
            </p>

            <h2>6. Security</h2>
            <p>
                Passwords are hashed with bcrypt. Sensitive uploads (ID, police
                record, TRN) are encrypted at rest in our object storage.
                Connections are TLS 1.2+. Access to personal data is limited to
                authorised SMART staff under an internal access policy.
            </p>

            <h2>7. Cookies</h2>
            <p>
                We use a single session cookie to keep you logged in. No
                third-party advertising trackers run on smartdemands247.com.
            </p>

            <h2>8. Complaints</h2>
            <p>
                If you believe we've mishandled your data, contact us first. If
                you remain unsatisfied, you can lodge a complaint with the
                <em> Office of the Information Commissioner of Jamaica </em>
                (<a href="https://www.oic.gov.jm" target="_blank" rel="noopener noreferrer" className="gold-text">oic.gov.jm</a>).
            </p>
        </PolicyShell>
    );
}
